import { useMemo } from 'react'
import { operationalSupplierIdAsNumber } from '@klear/operational'
import {
  orderEventForPaymentFromPayable,
  usePaymentPreviewAccounts
} from '@klear/order-event-ui/use-payment-preview-accounts'
import type { Payable } from '@klear/payable/payable'
import { PaymentPreview } from '@klear/ui/components/payment-preview'
import { Modal } from '@klear/ui/components/modal'

export type PayableCommitConfirmAction = 'complete' | 'publish'

const MODAL_CONFIG: Record<PayableCommitConfirmAction, { title: string; description: string; primaryText: string }> = {
  complete: {
    title: 'Send Payment Now?',
    description: 'Review the payment details before sending. This action cannot be undone.',
    primaryText: 'Send Now'
  },
  publish: {
    title: 'Schedule Payment?',
    description:
      'Review the payment details before scheduling. The payment will be sent on the scheduled date unless you complete it early.',
    primaryText: 'Schedule Payment'
  }
}

type PayableCommitConfirmModalProps = {
  isOpen: boolean
  action: PayableCommitConfirmAction
  payable: Payable
  isCommitting: boolean
  onClose: () => void
  onConfirm: () => void
}

export function PayableCommitConfirmModal({
  isOpen,
  action,
  payable,
  isCommitting,
  onClose,
  onConfirm
}: PayableCommitConfirmModalProps) {
  const paymentSource = useMemo(() => orderEventForPaymentFromPayable(payable), [payable])
  const supplierId = useMemo(
    () => operationalSupplierIdAsNumber(payable.primaryOperational?.supplierId ?? payable.supplierId),
    [payable]
  )
  const {
    payment: paymentPreview,
    sourceAccount,
    destinationAccount,
    isLoading: isAccountsLoading
  } = usePaymentPreviewAccounts(paymentSource, supplierId, isOpen)

  const config = MODAL_CONFIG[action]

  return (
    <Modal
      size="lg"
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !isCommitting) onClose()
      }}
      title={config.title}
      description={config.description}
      primaryText={isCommitting ? 'Working...' : config.primaryText}
      primaryDisabled={isCommitting}
      secondaryText="Cancel"
      secondaryDisabled={isCommitting}
      handleSecondaryAction={onClose}
      handlePrimaryAction={onConfirm}
    >
      {isAccountsLoading || !paymentPreview || !sourceAccount || !destinationAccount ? (
        <div className="text-sm text-muted-foreground">Loading payment preview...</div>
      ) : (
        <PaymentPreview
          payment={paymentPreview}
          sourceAccount={sourceAccount}
          destinationAccount={destinationAccount}
        />
      )}
    </Modal>
  )
}
